import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Menu, RefreshCw } from "lucide-react";

import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { AdminDashboardSection } from "@/components/admin/AdminDashboard";
import { UsersSection } from "@/components/admin/UsersSection";
import { PackagesSection } from "@/components/admin/PackagesSection";
import { TransactionsSection } from "@/components/admin/TransactionsSection";
import { InvestmentsSection } from "@/components/admin/InvestmentsSection";
import { KYCSection } from "@/components/admin/KYCSection";
import { SupportSection } from "@/components/admin/SupportSection";
import {
  CryptoAddressesSection,
  BankAccountSection,
  EmailProviderSection,
  TelegramSection,
  NotificationsSection,
  NotificationTemplatesSection,
  ManualPushSection,
} from "@/components/admin/SettingsSections";

const sectionTitles = {
  dashboard: "Dashboard",
  users: "User Management",
  packages: "Package Management",
  transactions: "Transactions",
  investments: "Investments",
  kyc: "KYC Verifications",
  support: "Support Tickets",
  crypto: "Crypto Addresses",
  bank: "Bank Account",
  email: "Email & SMS Provider",
  telegram: "Telegram Notifications",
  notifications: "Automated Notifications",
  templates: "Email Templates",
  push: "Send Notification",
};

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [loading, setLoading] = useState(true);

  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [kycVerifications, setKycVerifications] = useState([]);
  const [chatTickets, setChatTickets] = useState([]);
  const [cryptoSettings, setCryptoSettings] = useState({ btc_address: "", usdt_trc20_address: "", government_wallet: "" });
  const [bankSettings, setBankSettings] = useState({ bank_name: "", account_name: "", account_number: "", routing_number: "", swift_code: "", bank_address: "" });
  const [emailSettings, setEmailSettings] = useState({ smtp_email: "", smtp_password: "", smtp_host: "", smtp_port: 587, use_ssl: false });
  const [sendgridSettings, setSendgridSettings] = useState({ api_key: "", sender_email: "admin@andinvesttrading.com", sender_name: "" });
  const [twilioSettings, setTwilioSettings] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [telegramSettings, setTelegramSettings] = useState({ bot_token: "", chat_id: "" });
  const [emailNotifications, setEmailNotifications] = useState({
    welcome_email: true, deposit_confirmed: true, withdrawal_approved: true,
    withdrawal_rejected: true, investment_created: true, investment_matured: true,
    kyc_approved: true, kyc_rejected: true,
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const opts = { headers, withCredentials: true };
      const [statsRes, usersRes, packagesRes, transactionsRes, investmentsRes, emailRes, cryptoRes, kycRes, chatsRes, telegramRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, opts),
        axios.get(`${API}/admin/users`, opts),
        axios.get(`${API}/admin/packages`, opts),
        axios.get(`${API}/admin/transactions`, opts),
        axios.get(`${API}/admin/investments`, opts),
        axios.get(`${API}/admin/settings/email`, opts),
        axios.get(`${API}/admin/settings/crypto`, opts),
        axios.get(`${API}/admin/kyc-verifications`, opts),
        axios.get(`${API}/admin/chats`, opts),
        axios.get(`${API}/admin/settings/telegram`, opts),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data || []);
      setPackages(packagesRes.data || []);
      setTransactions(transactionsRes.data || []);
      setInvestments(investmentsRes.data || []);
      setKycVerifications(kycRes.data || []);
      setChatTickets(chatsRes.data || []);
      if (telegramRes.data) setTelegramSettings({ bot_token: telegramRes.data.bot_token || "", chat_id: telegramRes.data.chat_id || "" });
      if (emailRes.data) setEmailSettings(emailRes.data);
      if (cryptoRes.data) setCryptoSettings({ btc_address: cryptoRes.data.btc_address || "", usdt_trc20_address: cryptoRes.data.usdt_trc20_address || "", government_wallet: cryptoRes.data.government_wallet || "" });

      // Lazy load extra settings
      try {
        const [notifRes, sgRes, twRes, bankRes] = await Promise.all([
          axios.get(`${API}/admin/settings/notifications`, opts),
          axios.get(`${API}/admin/settings/sendgrid`, opts),
          axios.get(`${API}/admin/settings/twilio`, opts),
          axios.get(`${API}/admin/settings/bank-account`, opts),
        ]);
        if (notifRes.data?.notifications) setEmailNotifications((prev) => ({ ...prev, ...notifRes.data.notifications }));
        if (sgRes.data) setSendgridSettings({ api_key: sgRes.data.api_key || "", sender_email: sgRes.data.sender_email || "admin@andinvesttrading.com", sender_name: sgRes.data.sender_name || "" });
        if (twRes.data) setTwilioSettings({ account_sid: twRes.data.account_sid || "", auth_token: twRes.data.auth_token || "", from_number: twRes.data.from_number || "" });
        if (bankRes.data) setBankSettings({ bank_name: bankRes.data.bank_name || "", account_name: bankRes.data.account_name || "", account_number: bankRes.data.account_number || "", routing_number: bankRes.data.routing_number || "", swift_code: bankRes.data.swift_code || "", bank_address: bankRes.data.bank_address || "" });
      } catch {}
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const renderSection = () => {
    switch (activeSection) {
      case "dashboard":
        return <AdminDashboardSection stats={stats} />;
      case "users":
        return <UsersSection users={users} fetchData={fetchData} />;
      case "packages":
        return <PackagesSection packages={packages} fetchData={fetchData} />;
      case "transactions":
        return <TransactionsSection transactions={transactions} users={users} fetchData={fetchData} />;
      case "investments":
        return <InvestmentsSection investments={investments} users={users} packages={packages} fetchData={fetchData} />;
      case "kyc":
        return <KYCSection kycVerifications={kycVerifications} fetchData={fetchData} />;
      case "support":
        return <SupportSection chatTickets={chatTickets} fetchData={fetchData} />;
      case "crypto":
        return <CryptoAddressesSection cryptoSettings={cryptoSettings} fetchData={fetchData} />;
      case "bank":
        return <BankAccountSection bankSettings={bankSettings} fetchData={fetchData} />;
      case "email":
        return <EmailProviderSection emailSettings={emailSettings} sendgridSettings={sendgridSettings} twilioSettings={twilioSettings} fetchData={fetchData} />;
      case "telegram":
        return <TelegramSection telegramSettings={telegramSettings} fetchData={fetchData} />;
      case "notifications":
        return <NotificationsSection notifications={emailNotifications} fetchData={fetchData} />;
      case "templates":
        return <NotificationTemplatesSection fetchData={fetchData} />;
      case "push":
        return <ManualPushSection users={users} fetchData={fetchData} />;
      default:
        return <AdminDashboardSection stats={stats} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <AdminSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          user={user}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        <main className="flex-1 min-h-screen">
          {/* Header */}
          <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 px-6 py-4 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400 hover:text-slate-600">
                  <Menu className="w-6 h-6" />
                </button>
                <div>
                  <h1 className="font-heading text-xl font-semibold text-slate-800" data-testid="admin-page-title">{sectionTitles[activeSection] || "Admin Panel"}</h1>
                  <p className="text-slate-500 text-sm">Manage your platform</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="border-slate-300 text-slate-600 hover:bg-slate-100 gap-1.5" onClick={fetchData} data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
            </div>
          </header>

          {/* Content */}
          <div className="p-6">
            {renderSection()}
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminPage;
